// R1c.3a-v3 — Bid Proposal upload form. Multipart POST /api/bid-proposals.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Upload, FileText, X } from "lucide-react";

const MAX_BYTES = 25 * 1024 * 1024;
const ACCEPT = ".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export default function BidProposalNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [estimatedValue, setEstimatedValue] = useState("");
  const [submittedTo, setSubmittedTo] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const pickFile = (f) => {
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error(`File exceeds 25 MB limit (${(f.size / 1024 / 1024).toFixed(1)} MB).`);
      return;
    }
    const ext = f.name.split(".").pop()?.toLowerCase();
    if (!["pdf", "docx", "doc"].includes(ext)) {
      toast.error(`Unsupported file type .${ext}. Use PDF or DOCX.`);
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please attach a PDF or DOCX."); return; }
    if (!projectName.trim()) { toast.error("Project name is required."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("project_name", projectName.trim());
      if (estimatedValue) fd.append("estimated_value", estimatedValue);
      fd.append("submitted_to", submittedTo);
      fd.append("notes", notes);
      const r = await api.post("/bid-proposals", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded: ${r.data.project_name}`);
      navigate(`/crm/bid-proposals/${r.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <AdminAppShell
      activeNav="crm"
      title="Upload Bid Proposal"
      subtitle="Attach a PDF or DOCX up to 25 MB. Metadata visible in /crm/bid-proposals."
      breadcrumbs={[
        { label: "CRM", to: "/crm/leads" },
        { label: "Bid Proposals", to: "/crm/bid-proposals" },
        { label: "New", to: null },
      ]}
      user={user}
    >
      <form onSubmit={submit} className="max-w-2xl space-y-4" data-testid="bid-proposal-form">
        <div>
          <Label className="text-xs uppercase font-mono tracking-widest">Proposal file</Label>
          <div
            className={`mt-1 border-2 border-dashed rounded-md p-8 text-center transition-colors ${
              dragOver ? "border-erf-gold bg-erf-gold/10" : "border-erf-borderCream bg-white"
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault(); setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) pickFile(f);
            }}
            data-testid="bid-proposal-dropzone"
          >
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="w-6 h-6 text-erf-gold" aria-hidden="true" />
                <div className="text-left">
                  <div className="font-nav font-semibold text-erf-dark" data-testid="bid-proposal-file-name">{file.name}</div>
                  <div className="text-[11px] font-mono text-erf-dark/60">{(file.size / 1024 / 1024).toFixed(2)} MB · {file.type || "unknown type"}</div>
                </div>
                <button type="button" onClick={() => setFile(null)} className="p-1 hover:bg-erf-borderCream/40 rounded" aria-label="Remove file" data-testid="bid-proposal-file-clear">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 mx-auto text-erf-dark/40 mb-2" aria-hidden="true" />
                <div className="font-nav text-sm text-erf-dark/70">Drag a PDF or DOCX here, or</div>
                <label className="inline-block mt-2 cursor-pointer">
                  <span className="text-erf-gold font-mono text-sm hover:underline">browse to select</span>
                  <input
                    type="file"
                    accept={ACCEPT}
                    onChange={(e) => pickFile(e.target.files?.[0])}
                    className="hidden"
                    data-testid="bid-proposal-file-input"
                  />
                </label>
                <div className="text-[10px] font-mono text-erf-dark/50 mt-2">PDF · DOCX · max 25 MB</div>
              </>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="bid-project-name" className="text-xs uppercase font-mono tracking-widest">Project name *</Label>
          <Input id="bid-project-name" required value={projectName} onChange={(e) => setProjectName(e.target.value)}
                 className="mt-1" placeholder="Kitchen renovation – 123 Main St"
                 data-testid="bid-proposal-project-name" />
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="bid-value" className="text-xs uppercase font-mono tracking-widest">Estimated value (USD, optional)</Label>
            <Input id="bid-value" type="number" min="0" step="1" value={estimatedValue}
                   onChange={(e) => setEstimatedValue(e.target.value)}
                   className="mt-1 font-mono" placeholder="e.g. 45000"
                   data-testid="bid-proposal-value" />
          </div>
          <div>
            <Label htmlFor="bid-submitted-to" className="text-xs uppercase font-mono tracking-widest">Submitted to (comma-separated emails)</Label>
            <Input id="bid-submitted-to" value={submittedTo} onChange={(e) => setSubmittedTo(e.target.value)}
                   className="mt-1 font-mono" placeholder="client@example.com, pm@example.com"
                   data-testid="bid-proposal-submitted-to" />
          </div>
        </div>

        <div>
          <Label htmlFor="bid-notes" className="text-xs uppercase font-mono tracking-widest">Notes</Label>
          <Textarea id="bid-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                    className="mt-1" rows={4}
                    placeholder="Internal notes about this bid (scope, terms, contacts)…"
                    data-testid="bid-proposal-notes" />
        </div>

        <div className="flex gap-2 pt-2">
          <Button
            type="submit" disabled={busy || !file || !projectName.trim()}
            className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover"
            data-testid="bid-proposal-submit"
          >
            {busy ? "Uploading…" : "Upload proposal"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate("/crm/bid-proposals")}
                  data-testid="bid-proposal-cancel">
            Cancel
          </Button>
        </div>
      </form>
    </AdminAppShell>
  );
}
